import { useEffect, useRef, useState, useCallback } from "react";
import { db } from "../firebase/firebase";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "../components/SEO";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

// Canlı blog detay kapak kartı
const detailPalettes = [
  { from: "from-violet-900", via: "via-purple-800", to: "to-fuchsia-700", accent: "bg-fuchsia-400", text: "text-fuchsia-200" },
  { from: "from-slate-900", via: "via-cyan-900", to: "to-teal-700", accent: "bg-teal-400", text: "text-teal-200" },
  { from: "from-gray-900", via: "via-slate-800", to: "to-zinc-700", accent: "bg-slate-400", text: "text-slate-300" },
  { from: "from-rose-900", via: "via-pink-800", to: "to-red-700", accent: "bg-rose-400", text: "text-rose-200" },
  { from: "from-amber-900", via: "via-orange-800", to: "to-red-800", accent: "bg-amber-400", text: "text-amber-200" },
  { from: "from-indigo-900", via: "via-blue-800", to: "to-sky-700", accent: "bg-sky-400", text: "text-sky-200" },
];

function getDetailColor(title) {
  if (!title) return detailPalettes[0];
  let hash = 0;
  for (let i = 0; i < title.length; i++) hash = title.charCodeAt(i) + ((hash << 5) - hash);
  return detailPalettes[Math.abs(hash) % detailPalettes.length];
}

function BlogDetailCover({ title }) {
  const colors = getDetailColor(title);
  return (
    <div className={`relative w-full h-72 bg-gradient-to-br ${colors.from} ${colors.via} ${colors.to} overflow-hidden rounded-3xl shadow-xl`}>
      <div className={`absolute -top-12 -right-12 w-48 h-48 rounded-full ${colors.accent} opacity-10`} />
      <div className={`absolute top-20 -right-6 w-28 h-28 rounded-full ${colors.accent} opacity-[0.07]`} />
      <div className={`absolute -bottom-8 -left-8 w-36 h-36 rounded-full ${colors.accent} opacity-[0.08]`} />
      <div className={`absolute left-0 top-8 bottom-8 w-1.5 ${colors.accent} opacity-40 rounded-r-full`} />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 21px)`
      }} />
      <div className="relative z-10 flex flex-col justify-center h-full px-10 pl-12">
        <span className={`text-xs font-medium uppercase tracking-[0.2em] ${colors.text} opacity-70 mb-3`}>Blog Yazısı</span>
        <h2 className="text-white text-2xl sm:text-3xl font-bold leading-snug drop-shadow-sm">{title}</h2>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent" />
    </div>
  );
}

export default function BlogDetail() {
  const { id } = useParams();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);

  // ✅ TOAST STATE
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // ✅ CONFIRM TOAST STATE (for delete confirm etc.)
  const [confirmState, setConfirmState] = useState(null);
  // confirmState = { message, resolve }

  const showToast = (message, type = "info", duration = 2500) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });

    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, duration);
  };

  const toastConfirm = (message) => {
    return new Promise((resolve) => {
      setConfirmState({ message, resolve });
    });
  };

  const closeConfirm = (result) => {
    if (!confirmState?.resolve) return;
    confirmState.resolve(result);
    setConfirmState(null);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // 🔥 POST
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const docRef = doc(db, "posts", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setPost(docSnap.data());
      } catch (err) {
        console.error(err);
        showToast("Blog yüklenemedi.", "error");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPost();
  }, [id]);

  // 👑 ADMIN CHECK
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) return setIsAdmin(false);

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setIsAdmin(snap.data().role === "admin");
      } catch (err) {
        console.error(err);
      }
    };

    checkAdmin();
  }, [user]);

  // 🔥 COMMENTS
  const fetchComments = useCallback(async () => {
  try {
    const q = query(
      collection(db, "posts", id, "comments"),
      orderBy("createdAt", "asc")
    );

    const snap = await getDocs(q);

    setComments(
      snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
    );
  } catch (err) {
    console.error(err);
    showToast("Yorumlar yüklenemedi.", "error");
  }
}, [id]);

 useEffect(() => {
  if (id) fetchComments();
}, [id, fetchComments]);

  // ➕ COMMENT
  const handleComment = async (e) => {
    e.preventDefault();

    if (!user) return showToast("Yorum için giriş yapmalısın.", "error");
    if (comment.trim().length < 3) return showToast("Yorum çok kısa.", "error");

    try {
      await addDoc(collection(db, "posts", id, "comments"), {
        text: comment.trim(),
        userEmail: user.email,
        likes: 0,
        likedBy: [],
        createdAt: serverTimestamp(),
      });

      setComment("");
      showToast("Yorum gönderildi.", "success");
      fetchComments();
    } catch (err) {
      console.error(err);
      showToast("Yorum gönderilemedi.", "error");
    }
  };

  // 🗑️ DELETE (confirm toast)
  const deleteComment = async (commentId) => {
    const ok = await toastConfirm("Bu yorumu silmek istiyor musun?");
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "posts", id, "comments", commentId));
      showToast("Yorum silindi.", "success");
      fetchComments();
    } catch (err) {
      console.error(err);
      showToast("Yorum silinemedi.", "error");
    }
  };

  // ✏️ EDIT
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text);
  };

  const saveEdit = async (commentId) => {
    if (editText.trim().length < 3) return showToast("Çok kısa.", "error");

    try {
      await updateDoc(doc(db, "posts", id, "comments", commentId), {
        text: editText.trim(),
      });

      setEditingId(null);
      showToast("Yorum güncellendi.", "success");
      fetchComments();
    } catch (err) {
      console.error(err);
      showToast("Yorum güncellenemedi.", "error");
    }
  };

  // ❤️ LIKE TOGGLE
  const toggleLike = async (c) => {
    if (!user) return showToast("Beğenmek için giriş yap.", "error");

    const commentRef = doc(db, "posts", id, "comments", c.id);
    const alreadyLiked = c.likedBy?.includes(user.email);

    try {
      if (alreadyLiked) {
        await updateDoc(commentRef, {
          likes: increment(-1),
          likedBy: arrayRemove(user.email),
        });

        setComments((prev) =>
          prev.map((item) =>
            item.id === c.id
              ? {
                  ...item,
                  likes: Math.max((item.likes || 1) - 1, 0),
                  likedBy: item.likedBy.filter((e) => e !== user.email),
                }
              : item
          )
        );
      } else {
        await updateDoc(commentRef, {
          likes: increment(1),
          likedBy: arrayUnion(user.email),
        });

        setComments((prev) =>
          prev.map((item) =>
            item.id === c.id
              ? {
                  ...item,
                  likes: (item.likes || 0) + 1,
                  likedBy: [...(item.likedBy || []), user.email],
                }
              : item
          )
        );
      }
    } catch (err) {
      console.error("LIKE TOGGLE ERROR:", err);
      showToast("İşlem başarısız.", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-14 h-14 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return <div className="text-center mt-20">Blog bulunamadı</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-10 px-4"
    >
      {/* ✅ SEO (yalnızca yazı yüklendiğinde) */}
      <SEO
        title={`${post.title} | Antalya Psikolog Aysun Şeremet`}
        description={(post.content || "").slice(0, 155)}
        canonical={`https://aysunseremet.com/blog/${id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          image: post.imageUrl ? [post.imageUrl] : undefined,
          datePublished: post.createdAt?.seconds
            ? new Date(post.createdAt.seconds * 1000).toISOString()
            : undefined,
          author: {
            "@type": "Person",
            name: "Aysun Şeremet",
            jobTitle: "Uzman Klinik Psikolog",
            url: "https://aysunseremet.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Uzm. Klinik Psk. Aysun Şeremet",
            url: "https://aysunseremet.com",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://aysunseremet.com/blog/${id}`,
          },
        }}
      />

      {/* ✅ TOAST UI */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.98 }}
            className="fixed top-5 left-1/2 -translate-x-1/2 z-50"
          >
            <div
              className={`px-4 py-3 rounded-2xl shadow-xl text-sm text-white ${
                toast.type === "success"
                  ? "bg-green-600"
                  : toast.type === "error"
                  ? "bg-red-600"
                  : "bg-gray-900"
              }`}
            >
              {toast.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ CONFIRM TOAST UI */}
      <AnimatePresence>
        {confirmState && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => closeConfirm(false)}
            />
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-5"
            >
              <p className="text-sm text-gray-800">{confirmState.message}</p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => closeConfirm(false)}
                  className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800"
                >
                  Vazgeç
                </button>
                <button
                  onClick={() => closeConfirm(true)}
                  className="px-4 py-2 rounded-xl bg-red-600 text-white"
                >
                  Evet, Sil
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-3xl mx-auto space-y-8">
        {post.imageUrl ? (
          <motion.img
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            src={post.imageUrl}
            alt={post.title}
            className="w-full h-72 object-cover rounded-3xl shadow-xl"
          />
        ) : (
          <motion.div initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }}>
            <BlogDetailCover title={post.title} />
          </motion.div>
        )}

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
          <div className="text-sm text-gray-500 mt-1 space-y-0.5">
            <p>Yazan: <span className="font-semibold text-gray-700">Aysun ŞEREMET</span></p>
            <p>Uzman Klinik Psikolog</p>
            <p className="text-gray-400 mt-1">•{formatDate(post.createdAt)}</p>
          </div>
          <p className="mt-4 whitespace-pre-line">{post.content}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-xl font-bold mb-4">
            Yorumlar ({comments.length})
          </h2>

          <AnimatePresence>
            {comments.map((c) => {
              const isOwner = user?.email === c.userEmail;
              const canModify = isOwner || isAdmin;
              const liked = c.likedBy?.includes(user?.email);

              return (
                <motion.div
                  key={c.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-gray-50 rounded-2xl p-4 mb-3"
                >
                  <div className="flex justify-between">
                    <p className="text-xs text-gray-400">{c.userEmail}</p>

                    <div className="flex gap-3 items-center">
                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        whileHover={{ scale: 1.15 }}
                        onClick={() => toggleLike(c)}
                        className={`text-sm ${
                          liked ? "text-red-500" : "text-pink-600"
                        }`}
                      >
                        {liked ? "💔" : "❤️"} {c.likes || 0}
                      </motion.button>

                      {isOwner && (
                        <button
                          onClick={() => startEdit(c)}
                          className="text-xs text-blue-600"
                        >
                          Düzenle
                        </button>
                      )}

                      {canModify && (
                        <button
                          onClick={() => deleteComment(c.id)}
                          className="text-xs text-red-600"
                        >
                          Sil
                        </button>
                      )}
                    </div>
                  </div>

                  {editingId === c.id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full border rounded-xl p-2"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(c.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg"
                        >
                          Kaydet
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1 bg-gray-400 text-white rounded-lg"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 mt-2">{c.text}</p>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          <div className="mt-4 space-y-3">
  {!user && (
    <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700 ring-1 ring-amber-200">
      Yorum yapmak için giriş yapmalısınız.
    </div>
  )}

  <form onSubmit={handleComment} className="space-y-2">
    <textarea
      value={comment}
      onChange={(e) => setComment(e.target.value)}
      className="w-full border rounded-2xl p-3 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      placeholder={user ? "Yorum yaz..." : "Yorum yapmak için giriş yapın"}
      disabled={!user}
    />
    <button
      type="submit"
      disabled={!user}
      className="bg-pink-500 text-white px-4 py-2 rounded-xl disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
    >
      Yorumu Gönder
    </button>
  </form>
</div>
        </div>
      </div>
    </motion.div>
  );
}
